/**
 * Transaction submit, approve, and status-poll pipeline.
 *
 * Sits between the write helpers in the api layer and the store. A send is two
 * authenticated calls the gateway fulfills server-side: a create that opens a
 * pending approval, then an approve that drives the MPC sign and broadcast once
 * the approval quorum is met. For a single-approver self-custody wallet the
 * creating user may approve their own transaction, so this one pipeline completes
 * the send for one user. It holds no key, builds no payload, and makes no
 * authorization decision of its own; the gateway is the spend authority.
 */

import {
  createTransaction as apiCreateTransaction,
  approveApproval as apiApproveApproval,
  getApprovals as apiGetApprovals,
  getTransaction as apiGetTransaction,
  ApiError,
} from '@/lib/api';
import { mapActivity, messageFor } from '@/services/wallets';
import { prependActivity } from '@/store/store';
import { deviceAssertionHeaderForApproval } from '@/services/devices';

/**
 * A gateway TransactionResponse record. The api helpers return the wire shape
 * verbatim; only the fields this pipeline reads are pinned, and the index
 * signature admits the rest of the record.
 */
interface TransactionRecord {
  id: string;
  status?: string;
  [key: string]: unknown;
}

/**
 * A gateway approval record. Only the fields this pipeline matches on are
 * pinned; the index signature admits the rest.
 */
interface ApprovalRecord {
  id: string;
  transaction_id?: string;
  status?: string;
  [key: string]: unknown;
}

/**
 * Options shared by the pipeline functions. The abort signal cancels the
 * in-flight calls and the poll loop when the view is torn down.
 */
interface PipelineOptions {
  signal?: AbortSignal;
}

/**
 * The CreateTransactionRequest body the confirm step built. Passed through to
 * the gateway verbatim, so its exact fields are the gateway's contract rather
 * than this module's.
 */
type CreateTransactionRequest = Record<string, unknown>;

/**
 * Where the pipeline stopped: a completed approve, a policy hold, a missing
 * approval row the user must act on elsewhere, or the initial submit.
 */
export type SendStage = 'submitted' | 'pending_policy' | 'approved' | 'no_approval';

/**
 * The outcome of a submit-and-approve run, surfaced to the view so it can render
 * the right status panel.
 */
export interface SendOutcome {
  /** The latest TransactionResponse record. */
  transaction: TransactionRecord;
  /**
   * Where the pipeline stopped: a completed approve, a policy hold, or a missing
   * approval row the user must act on elsewhere.
   */
  stage: SendStage;
}

/**
 * Transaction statuses past which no further polling is useful on any chain.
 * Reaching any of these stops the poll loop.
 */
const TERMINAL = new Set<string>(['broadcast', 'confirmed', 'failed', 'rejected']);

/**
 * Chains whose pipeline ends at "signed" on this deployment. Solana is
 * sign-only: the gateway signs but does not broadcast, so "signed" is the
 * successful terminal status there and polling past it would only run out the
 * poll budget.
 */
const SIGN_ONLY_CHAINS = new Set<string>(['solana']);

/**
 * Whether a transaction status is terminal for polling purposes. The shared
 * terminal set ends every chain's poll; on a sign-only chain "signed" also ends
 * it, as the successful outcome rather than a waypoint.
 *
 * @param status The transaction status.
 * @param chain The request's chain, used for the sign-only special case.
 * @returns True when polling past this status is useless.
 */
export function isTerminalStatus(status: string, chain?: string): boolean {
  if (TERMINAL.has(status)) return true;
  return status === 'signed' && SIGN_ONLY_CHAINS.has(String(chain ?? '').toLowerCase());
}

/**
 * Thrown when the gateway's policy engine refuses a transaction outright at
 * create with a 403. Carries a calm, actionable message the Send status panel
 * can show as-is: an expired session surfaces as a 401 the api layer already
 * refreshes and replays, so a 403 here is a policy decision, not an auth
 * failure.
 */
export class PolicyRefusedError extends Error {
  constructor() {
    super(
      'This transaction was refused by your organization policy, so nothing was ' +
        'signed or sent. Review the policy limits, or ask an organization admin ' +
        'if this looks wrong.',
    );
    this.name = 'PolicyRefusedError';
  }
}

/**
 * Statuses that mean the gateway is waiting on a policy step beyond a single
 * approval, so the pipeline stops after create rather than self-approving.
 */
const PENDING_POLICY = 'pending_policy';

/** Default poll budget: ninety ticks at two seconds is roughly three minutes. */
const POLL_TICKS = 90;
const POLL_INTERVAL_MS = 2000;

/**
 * Submit a transaction, then drive it through approval so the gateway signs and
 * broadcasts. Each step updates the store so the activity surface reflects
 * progress live.
 *
 * @param req The CreateTransactionRequest body the confirm step built.
 * @param opts Options; the abort signal cancels the in-flight calls and the
 *   poll loop when the view is torn down.
 * @returns The pipeline outcome.
 */
/**
 * The pipeline stage an error escaped from, stamped onto thrown errors so a
 * caller can tell a CLEAN failure (nothing signed: 'create', 'find_approval',
 * 'device') from an AMBIGUOUS one ('approve', 'poll' — the gateway may have
 * signed and broadcast before the response was lost). The swap path uses this
 * to keep a no-retry safety record; the send path ignores it, so stamping is
 * behavior-neutral there.
 */
export type SendStageReached = 'create' | 'find_approval' | 'device' | 'approve' | 'poll';

/** Stamp and rethrow. Never overwrites a stage set deeper in the call. */
function stampStage(err: unknown, stage: SendStageReached, transactionId?: string): never {
  if (err && typeof err === 'object') {
    if (!('stageReached' in err)) {
      (err as { stageReached?: SendStageReached }).stageReached = stage;
    }
    // The gateway transaction id, once known, rides the SAME error the stage
    // stamp does (B1): an ambiguous escape that carries the id lets the swap
    // path keep polling the REAL transaction — the honest way to resolve
    // "possibly live" — instead of latching a record nothing can advance.
    // Never overwritten for the same reason the stage is not: the deepest
    // stamp is the truthful one.
    if (transactionId && !('transactionId' in err)) {
      (err as { transactionId?: string }).transactionId = transactionId;
    }
  }
  throw err;
}

export async function submitAndApprove(
  req: CreateTransactionRequest,
  opts: PipelineOptions = {},
): Promise<SendOutcome> {
  const { signal } = opts;

  // Step one: create the transaction record and its pending approval. A 403
  // here is the policy engine refusing the transaction itself, so it is
  // translated to the calm policy message rather than surfacing a raw gateway
  // string or the shared session-expired wording.
  let tx: TransactionRecord;
  try {
    tx = (await apiCreateTransaction(req, { signal })) as TransactionRecord;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && !err.aborted) {
      stampStage(new PolicyRefusedError(), 'create');
    }
    stampStage(err, 'create');
  }
  prependActivity(mapActivity(tx));

  // A policy step-up or time-lock lands in pending_policy. Additional approvals
  // or a release time are required, so the pipeline stops here and surfaces the
  // hold rather than approving past a control the gateway is enforcing.
  if (String(tx.status) === PENDING_POLICY) {
    return { transaction: tx, stage: 'pending_policy' };
  }

  // Step two: find the approval the gateway opened for this transaction. The
  // approval record carries the transaction id, so the match is exact and never
  // approves a different pending transaction.
  let approval;
  try {
    approval = await findApprovalForTransaction(tx.id, { signal });
  } catch (err) {
    stampStage(err, 'find_approval', tx.id);
  }
  if (!approval) {
    // The approval row is the server's record of what may be signed. If it is
    // absent, do not fabricate a signing trigger; surface the pending state so
    // the user can act from an approvals surface rather than guessing.
    return { transaction: tx, stage: 'no_approval' };
  }

  // Step three: approve. The single-approver self-custody flow sends no
  // cryptographic approval signature; the gateway allows the creator to approve
  // their own transaction when only one approval is required. When the user has
  // an enrolled device passkey, run the device assertion first and carry it as
  // the X-Device-Assertion header so the gateway can verify the transaction was
  // authorized on this device before it signs. A user who cancels the device
  // prompt stops the send rather than approving without the stamp.
  let deviceHeaders;
  try {
    deviceHeaders = await deviceAssertionHeaderForApproval(approval.id, signal);
  } catch (err) {
    // A cancelled or failed device prompt stops the send BEFORE anything is
    // signed — a clean failure, not an ambiguous one.
    stampStage(err, 'device', tx.id);
  }
  try {
    await apiApproveApproval(approval.id, {}, { signal, headers: deviceHeaders });
  } catch (err) {
    // THE IRREVERSIBLE BOUNDARY. Approve triggers sign + broadcast; an error
    // here (or any later) may mean the transaction is already on its way.
    stampStage(err, 'approve', tx.id);
  }

  // Step four: poll to a terminal state for status feedback. The request's
  // chain rides along so a sign-only chain's poll ends at "signed" instead of
  // waiting for a broadcast that never comes.
  let last;
  try {
    last = await pollTransaction(tx.id, {
      signal,
      chain: typeof req.chain === 'string' ? req.chain : undefined,
    });
  } catch (err) {
    // Approve already SUCCEEDED — losing the poll does not un-broadcast it.
    stampStage(err, 'poll', tx.id);
  }
  return { transaction: last, stage: 'approved' };
}

/**
 * Find the pending approval row for a transaction by its id. Lists the org's
 * approvals and matches on the transaction id the approval carries, so a wallet
 * with several pending transactions never approves the wrong one.
 *
 * @param txId Transaction uuid.
 * @param opts Options.
 * @returns The matching approval, or null when none is pending for this
 *   transaction.
 */
export async function findApprovalForTransaction(
  txId: string,
  opts: PipelineOptions = {},
): Promise<ApprovalRecord | null> {
  const rows = (await apiGetApprovals({ signal: opts.signal })) as ApprovalRecord[] | unknown;
  if (!Array.isArray(rows)) return null;
  return (
    (rows as ApprovalRecord[]).find(
      (a) => String(a.transaction_id) === String(txId) && String(a.status) === 'pending',
    ) ?? null
  );
}

/**
 * Poll one transaction until it reaches a terminal state or the budget expires.
 * Updates the store on each tick so the activity surface advances live. A caller
 * abort (the view tearing down) stops the loop and returns the last observation.
 *
 * @param txId Transaction uuid.
 * @param opts Options. The optional chain refines what counts as terminal: a
 *   sign-only chain ends at "signed" (see {@link isTerminalStatus}).
 * @returns The last observed TransactionResponse.
 */
export async function pollTransaction(
  txId: string,
  opts: PipelineOptions & { chain?: string } = {},
): Promise<TransactionRecord> {
  const { signal } = opts;
  let last: TransactionRecord | null = null;
  for (let i = 0; i < POLL_TICKS; i += 1) {
    if (signal?.aborted) {
      return last ?? ((await apiGetTransaction(txId)) as TransactionRecord);
    }
    last = (await apiGetTransaction(txId, { signal })) as TransactionRecord;
    prependActivity(mapActivity(last));
    if (isTerminalStatus(String(last.status), opts.chain)) return last;
    await delay(POLL_INTERVAL_MS, signal);
  }
  // The loop runs at least once, so last is always set by the time it exits.
  return last as TransactionRecord;
}

/**
 * Resolve after a delay, or immediately when the signal aborts, so a poll loop
 * stops promptly on teardown rather than waiting out the interval.
 *
 * @param ms Milliseconds to wait.
 * @param signal Abort signal that resolves the wait early.
 * @returns Resolves after the delay or on abort.
 */
function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true },
      );
    }
  });
}

export { messageFor };
